/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import Fuse from 'fuse.js';
import EntityLabel from '@ion/core/src/entities/EntityLabel';
import {info} from '@ion/core/src/logger/logger';
import {
  AccountLink,
  SubAccountLink
} from '@ion/core/src/types/entities';
import {
  FilteredAccount
} from '@ion/core/src/types/general';
import getGlobalProp from '@ion/dls/src/props/GlobalPropsConfig';

export const Page_LIMIT  = 25;
const minMatchCharLength = getGlobalProp('global-groups.search.minMatchCharLength');
const DEFAULT_MIN_MATCH_CHAR_LENGTH = 3;

export class GlobalGroupsSearch {
  private _accounts: AccountLink[] = [];
  private _searchableAccounts: FilteredAccount[] = [];
  private _fuseSearch: Fuse<FilteredAccount> | null = null;
  private _searchResults = [];
  private _fuseOptions = {
    isCaseSensitive    : false,
    findAllMatches     : true,
    matchAllTokens     : true,
    shouldSort         : true,
    includeScore       : true,
    includeMatches     : false,
    maxPatternLength   : 32,
    minMatchCharLength : minMatchCharLength ?? DEFAULT_MIN_MATCH_CHAR_LENGTH,
    threshold          : 0.4,
    useExtendedSearch  : false,
    ignoreLocation     : true,
    keys               : ['title', {name: 'accountId', weight: 0.3}]
  };

  private static weightMap = {
    2 : 2,
    1 : 0.3
  };

  public setAccounts($accounts: AccountLink[]) {
    this._accounts = $accounts;
    this._processAccounts();
  }

  public getAccounts() {
    return this._accounts;
  }

  public getSearchableAccounts() {
    return this._searchableAccounts;
  }

  public updateSearchableKeys(keys: string[]): void {
    this._fuseOptions.keys = [...keys];
  }

  // onlyAccountId option is used by groups tree filter in iOnSuite tracking and AHR
  public findResults(searchTerm: string, onlyAccountId = false) {
    const start = new Date().getTime();
    if (!onlyAccountId) {
      if (minMatchCharLength && searchTerm.length < DEFAULT_MIN_MATCH_CHAR_LENGTH) {
      // force plain string search for single/double characters...
        this._searchResults = this._fuseSearch?.search(searchTerm);
      } else if (parseInt(searchTerm)) {
        this._searchResults = this._fuseSearch?.search({
          $or : [
            {title: `'${searchTerm}`},
            {accountId: `'${searchTerm}`}
          ]
        });
      } else {
        this._searchResults = this._fuseSearch?.search({title: `/${searchTerm}/`})?.sort((account1, account2) => {
          const acc1       = account1.item.title.toLowerCase();
          const acc2       = account2.item.title.toLowerCase();
          const searchText = searchTerm.toLowerCase();
          return Number(acc2.includes(searchText)) - Number(acc1.includes(searchText));
        });
      }
    } else {
      this._searchResults = this._fuseSearch?.search({accountId: `'${searchTerm}`});
    }
    const end = new Date().getTime();
    const time = end - start;
    info(`Execution time for accounts search is: ${time} for ${this.fetchResultsCount()} results`);
  }

  public fetchResultsCount(): number {
    return this._searchResults.length;
  }

  public fetchResults(page: number, pageLimit = Page_LIMIT) {
    const displayableItemsLength = page*pageLimit + 1;
    return this._searchResults.slice(0, displayableItemsLength);
  }

  private _processAccounts() {
    const searchableAccounts: FilteredAccount[] = [];
    const visited = new Set<string>();
    const n = this._accounts?.length || 0;
    for (let i = 0; i < n; i++) {
      const accounts = this._separateSubAccounts(this._accounts[i], [], true, visited);
      const len = accounts.length;
      for (let j = 0; j < len; j++) {
        searchableAccounts.push(accounts[j]);
      }
    }
    this._searchableAccounts = searchableAccounts;
    const indexes = Fuse.createIndex([{
      name   : 'title',
      weight : GlobalGroupsSearch.weightMap[2]
    }, {
      name   : 'accountId',
      weight : GlobalGroupsSearch.weightMap[1]
    }], searchableAccounts);
    this._fuseSearch = new Fuse(searchableAccounts, this._fuseOptions, indexes);
  }

  // eslint-disable-next-line max-params
  private _separateSubAccounts(
    account: AccountLink,
    currentPath: string[],
    isRoot: boolean,
    visited?: Set<string>
  ): FilteredAccount[] {
    const result: FilteredAccount[] = [];
    const {
      title,
      href,
      status,
      subAccounts
    } = account;
    const accountTitle = EntityLabel.get(account);
    const path = isRoot ? [accountTitle]: [...currentPath, accountTitle];
    result.push({
      path,
      title,
      status,
      href,
      accountId : href.split('/').slice(-1)[0]
    });
    const n = subAccounts?.length;
    if (n) {
      const isParent = this._isSubAccountType(subAccounts[0]);
      for (let i = 0; i < n; i++) {
        const items = isParent ? subAccounts[i].subaccount : subAccounts[i];
        const accountId = items.href.split('/').slice(-1)[0];
        if (!visited?.has(accountId)) {
          visited?.add(accountId);
          const subResults = this._separateSubAccounts(items, [...path], false, visited);
          const len = subResults.length;
          for (let j = 0; j < len; j++) {
            result.push(subResults[j]);
          }
        }
      }
    }
    return result;
  }

  private _isSubAccountType(object: any): object is SubAccountLink {
    return 'subaccount' in object;
  }

  private _isAccountType(object: any): object is AccountLink {
    return !('subaccount' in object);
  }

  private static _getWeight(pathLength: number): number {
    return GlobalGroupsSearch.weightMap[pathLength] || 5;
  }
}

export default new GlobalGroupsSearch();
